import { motion, AnimatePresence } from 'framer-motion';
import type { CommunicationMode } from '../types/app';

interface DegradationPromptProps {
  isVisible: boolean;
  currentMode: CommunicationMode;
  suggestedMode: CommunicationMode;
  onAccept: () => void;
  onDismiss: () => void;
}

const modeLabels: Record<CommunicationMode, string> = {
  video: 'Video',
  audio: 'Audio',
  chat: 'Chat',
};

function getTitle(currentMode: CommunicationMode): string {
  switch (currentMode) {
    case 'video':
      return 'Video Unavailable';
    case 'audio':
      return 'Audio Unavailable';
    default:
      return 'Connection Issue';
  }
}

function getBody(currentMode: CommunicationMode, suggestedMode: CommunicationMode): string {
  const currentLabel = modeLabels[currentMode].toLowerCase();
  const suggestedLabel = modeLabels[suggestedMode].toLowerCase();
  return `We're having trouble with your ${currentLabel} connection. Would you like to continue with ${suggestedLabel} instead?`;
}

/**
 * Modal dialog suggesting mode fallback (video -> audio -> chat).
 *
 * Styled in luxury theme with two options:
 *   - "Continue with [mode]" (primary, gold button)
 *   - "Try Again" (secondary, outline button)
 *
 * State preservation: cart, chat messages, and products persist through mode switches.
 */
export function DegradationPrompt({
  isVisible,
  currentMode,
  suggestedMode,
  onAccept,
  onDismiss,
}: DegradationPromptProps) {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          data-testid="degradation-prompt"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl max-w-md w-full p-6"
          >
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg
                  className="h-6 w-6 text-amber-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line x1="12" y1="9" x2="12" y2="13" strokeLinecap="round" />
                  <line x1="12" y1="17" x2="12.01" y2="17" strokeLinecap="round" />
                </svg>
              </div>
            </div>

            <h2 className="text-xl font-serif text-brand-navy text-center mb-2">
              {getTitle(currentMode)}
            </h2>

            <p className="text-gray-600 text-center mb-6 text-sm">
              {getBody(currentMode, suggestedMode)}
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={onAccept}
                className="w-full py-3 bg-brand-gold text-white font-medium rounded-lg hover:bg-brand-gold-dark transition-colors"
              >
                Continue with {modeLabels[suggestedMode]}
              </button>

              <button
                onClick={onDismiss}
                className="w-full py-3 border border-brand-gold text-brand-gold font-medium rounded-lg hover:bg-brand-gold/5 transition-colors"
              >
                Try Again
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
